import { useCallback, useState } from 'react';
import { isEmpty, isNil } from 'lodash';

import type { SendingMessage } from '../../conversation/types';

/** Why the last message did not go: the failure's reason, null where it gave none. */
interface SendError {
  reason: string | null;
}

export interface ComposerSend {
  value: string;
  setValue: (value: string) => void;
  /** The message on its way to Clive, from the send until the server answers; null otherwise. */
  inFlight: SendingMessage | null;
  isSending: boolean;
  /** The last message did not go; null once one goes or the text changes. */
  error: SendError | null;
  submit: () => Promise<void>;
}

/**
 * The composer's text and its one send. The text leaves the field as it is sent, and is the message on its way
 * until the server answers. A failed send puts the text back in the field, as the handler wrote it, with the
 * failure, so nothing the handler wrote is lost to it.
 */
export const useComposerSend = (send: (message: SendingMessage) => Promise<void>): ComposerSend => {
  const [value, setText] = useState('');
  const [inFlight, setInFlight] = useState<SendingMessage | null>(null);
  const [error, setError] = useState<SendError | null>(null);

  const submit = useCallback(async () => {
    const text = value.trim();
    if (isEmpty(text)) return;

    const message = { text, sentAt: new Date().toISOString() };
    setInFlight(message);
    setText('');
    setError(null);
    try {
      await send(message);
    } catch (reason) {
      setText(value);
      setError({ reason: reason instanceof Error && !isEmpty(reason.message) ? reason.message : null });
    } finally {
      setInFlight(null);
    }
  }, [send, value]);

  // A failure is about the text that failed to go, so it goes once the handler changes that text.
  const setValue = useCallback((text: string) => {
    setText(text);
    setError(null);
  }, []);

  return { value, setValue, inFlight, isSending: !isNil(inFlight), error, submit };
};
